"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { ActivityItem } from "@/data/types";
import { useData } from "@/i18n/LangContext";

interface ActivityShowcaseProps {
  title: string;
  note: string;
  items: ActivityItem[];
}

const accentStyles = {
  green: {
    badge: "bg-[#88ff88]/10 text-[#88ff88] border-[#88ff88]/30"
  },
  blue: {
    badge: "bg-[#88aaff]/10 text-[#88aaff] border-[#88aaff]/30"
  }
};

export function ActivityShowcase({ title, note, items }: ActivityShowcaseProps) {
  const { ui } = useData();
  return (
    <motion.section
      className="mb-16"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      data-cy="activity-showcase"
    >
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#88ff88]">{title}</h2>
        <span className="text-xs text-white/40">{note}</span>
      </div>

      <div className="flex flex-col gap-6">
        {items.map((item, index) => {
          const accent = accentStyles[item.accent];
          const card = (
            <motion.div
              key={item.name}
              className="bg-[rgba(10,32,25,0.5)] backdrop-blur-sm p-4 md:p-6 rounded-xl border border-[#88ff88]/20 hover:border-[#88ff88]/40 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 mb-4">
                <span className={`self-start px-3 py-1 rounded-full text-sm font-semibold border ${accent.badge}`}>
                  {item.name}
                </span>
                <p className="text-white/70 text-sm">{item.description}</p>
              </div>
              <div className="rounded-lg overflow-hidden border border-white/5">
                <Image
                  src={item.image}
                  alt={ui.about.activityHeatmapAlt.replace('{name}', item.name)}
                  width={item.imageWidth}
                  height={item.imageHeight}
                  className="w-full h-auto"
                  sizes="(max-width: 1152px) 100vw, 1152px"
                />
              </div>
            </motion.div>
          );

          return item.link ? (
            <a
              key={item.name}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={ui.about.activityProfileAria.replace('{name}', item.name)}
            >
              {card}
            </a>
          ) : (
            card
          );
        })}
      </div>
    </motion.section>
  );
}
